import {
  ActionButton,
  buildConfirmTimeSeriesSchema,
  ConfirmTimeSeriesOutputType,
  ConfirmTimeSeriesType,
  DataObjectGroupType,
  Form,
  FormAlerts,
  GetApiErrorMessage,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useAlertContext,
  useDataObjectGroupsWithDescriptionsQuery,
  useGroupDescriptionOptions,
  useMutationErrors,
  useSystemClassifierItems,
  useSystemClassifierSelectOptions,
  useConfirmTimeSeriesOutputData,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import React, { FC, useMemo } from 'react';
import translations from './TimeSeriesActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TimeSeriesOutputsTable } from './TimeSeriesOutputsTable';
import { useWatch } from 'react-hook-form';
import { OutputQuickSelect } from './OutputQuickSelect';

const { addTranslations, t, exists } = i18nNsFactory(
  'time-series-action-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export interface TimeSeriesConfirmationModalProps {
  mode: 'default' | 'edit' | 'calculate';
  onClose: () => void;
}

export const TimeSeriesConfirmationModal: React.FC<
  TimeSeriesConfirmationModalProps
> = ({ mode, onClose }) => {
  const { mutateAsync } = useConfirmTimeSeriesOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const sourceDataVersionItems = useSystemClassifierItems(
    'SOURCE_DATA_VERSION',
  );
  const confirmationStatusItems = useSystemClassifierItems(
    'CONFIRMATION_STATUS',
  );
  const fixedSourceDataVersionId =
    mode !== 'calculate'
      ? sourceDataVersionItems.find((item) => item.code === 'LATEST')?.id
      : undefined;
  const calculateSchema = useMemo(
    () =>
      buildConfirmTimeSeriesSchema(
        sourceDataVersionItems,
        confirmationStatusItems,
        fixedSourceDataVersionId,
      ),
    [sourceDataVersionItems, confirmationStatusItems, fixedSourceDataVersionId],
  );

  return (
    <Form
      schema={calculateSchema}
      defaultValues={{ outputs: [] }}
      onSubmit={(data: ConfirmTimeSeriesType) => {
        resetAlert();
        clearMutationErrors();

        return mutateAsync({
          data: {
            ...data,
            change: mode === 'edit',
            calculate: mode === 'calculate',
          },
        })
          .then(() => {
            onClose();
            setSuccess(t(getSuccessKeyByMode(mode)));
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <TimeSeriesConfirmationModalInner mode={mode} onClose={onClose} />
    </Form>
  );
};

const TimeSeriesConfirmationModalInner: FC<{
  mode: 'default' | 'edit' | 'calculate';
  onClose: () => void;
}> = (props) => {
  const { mode, onClose } = props;
  const outputs = useWatch<{ outputs: ConfirmTimeSeriesOutputType[] }>({
    name: 'outputs',
  }) as ConfirmTimeSeriesOutputType[] | undefined;
  const { data: groupsWithDescriptions, isFetching } =
    useDataObjectGroupsWithDescriptionsQuery({
      groupType: DataObjectGroupType.O,
      active: true,
    });
  const groupDescriptionOptions = useGroupDescriptionOptions(
    groupsWithDescriptions,
    true,
  );
  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION']);
  const confirmationStatusOptions = useSystemClassifierSelectOptions([
    'CONFIRMATION_STATUS',
  ])['CONFIRMATION_STATUS'];

  return (
    <Modal
      onCancel={onClose}
      width={mode === 'calculate' ? '80rem' : '60rem'}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              disabled={!outputs || outputs.length === 0}
              label={t(getButtonKeyByMode(mode))}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{t('title')}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <OutputQuickSelect
          options={groupDescriptionOptions}
          isLoading={isFetching}
          sourceDataVersionOptions={
            mode === 'calculate' ? sourceDataVersionOptions : undefined
          }
        />
        <Padding top>
          <TimeSeriesOutputsTable<ConfirmTimeSeriesOutputType>
            groupsWithDescriptionsOptions={groupDescriptionOptions}
            isLoading={isFetching}
            columnConfig={{
              ...(mode === 'calculate'
                ? {
                    sourceDataVersionId: {
                      accessor: (
                        row: ConfirmTimeSeriesOutputType & {
                          sourceDataVersionId?: number;
                        },
                      ) => row.sourceDataVersionId,
                      options: sourceDataVersionOptions,
                    },
                  }
                : {}),
              confirmationStatusId: {
                accessor: (row) => row.statusId,
                options: confirmationStatusOptions,
              },
              confirmationComment: {
                accessor: (row) => row.comment,
              },
            }}
          />
        </Padding>
      </Padding>
    </Modal>
  );
};

const getButtonKeyByMode = (mode: 'default' | 'edit' | 'calculate') => {
  if (mode === 'edit') return 'button.changeConfirmStatus';
  if (mode === 'calculate') return 'button.confirmAndCalculate';
  return 'button.confirm';
};

const getSuccessKeyByMode = (mode: 'default' | 'edit' | 'calculate') => {
  if (mode === 'edit') return 'alert.success.edited';
  if (mode === 'calculate') return 'alert.success.confirmedAndCalculated';
  return 'alert.success.confirmed';
};
